import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Scale, FileCheck, KeyRound } from "lucide-react";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";

const steps = [
    {
        icon: MessageCircle,
        num: "01",
        title: "Free consultation",
        text: "We start with a free, no-obligation conversation — by phone or video call, wherever you are in Australia. You tell us your goals and your numbers; we explain your options in plain English."
    },
    {
        icon: Scale,
        num: "02",
        title: "Compare lenders",
        text: "We compare your situation across more than 60 Australian lenders — major banks, mutuals, non-bank and specialist lenders — and recommend the loan structure that fits, with the reasoning in writing."
    },
    {
        icon: FileCheck,
        num: "03",
        title: "Application",
        text: "Once you're happy, we prepare and lodge your application, handle the lender's questions and keep you updated through assessment — no chasing banks, no paperwork ping-pong."
    },
    {
        icon: KeyRound,
        num: "04",
        title: "Settlement",
        text: "We coordinate with your conveyancer or solicitor right through to settlement day — and we stay in touch afterwards, reviewing your loan as rates and lender policies change."
    }
];

export default function HowItWorksPage() {
    return (
        <div data-testid="page-how-it-works" className="pt-20">
            <Seo
                title="How It Works | Finance Wealth Mortgage Broker Australia"
                description="How working with Finance Wealth goes: a free consultation, comparison across 60+ Australian lenders, a fully managed application, and support through settlement and beyond."
                keywords="how mortgage broker works, mortgage broker process Australia, home loan process, Finance Wealth process"
                canonicalPath="/how-it-works"
            />
            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">The process</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            How it works.
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">
                            Four simple steps from first conversation to settlement — handled for you,
                            Australia-wide, online and over the phone.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {steps.map((s, i) => (
                            <div key={s.num} data-testid={`how-step-${i + 1}`} className="bg-white border border-border rounded-sm p-8 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                                <div className="flex items-center justify-between">
                                    <div className="w-12 h-12 rounded-sm bg-[#1A362B] text-white grid place-items-center">
                                        <s.icon className="w-5 h-5" />
                                    </div>
                                    <div className="font-serif text-4xl text-[#1A362B]/20">{s.num}</div>
                                </div>
                                <h2 className="mt-6 font-serif text-2xl text-[#1A362B] tracking-tight">{s.title}</h2>
                                <p className="mt-3 text-[#1C1C1C]/75 leading-relaxed">{s.text}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 max-w-3xl">
                        <div className="divider-thin my-8" />
                        <p className="text-[#1C1C1C]/80 leading-relaxed">
                            For residential home loans, our service costs you nothing — lenders pay us a commission
                            once your loan settles, and the details are disclosed to you in writing before you proceed.
                            Want the detail first? Read our <Link to="/services" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">services</Link> or
                            explore the <Link to="/calculator" className="text-[#1A362B] font-medium underline decoration-[#C69C6D] underline-offset-2 hover:text-[#C69C6D] transition">repayment calculator</Link>.
                        </p>
                        <Link to="/contact" data-testid="how-it-works-cta" className="btn-primary mt-8">
                            Start with a free consultation <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <CtaStrip />
        </div>
    );
}
